import React, { useCallback, useEffect, useRef } from 'react';
import { Flex } from '~/ui/flex/Flex';
import { Overlay } from '~/ui/overlay/Overlay';
import { Card } from '~/ui/card/Card';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { Root, DOCUMENTS_DIALOG_TITLE_ID } from './Root';
import { DocumentsProvider, useDocumentsStore } from './Store';
import PoliciesIcon from '~/assets/icons/policies.svg';
import styled from '@xstyled/styled-components';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '../state';
import { useOpenCloseAudioEffect } from '~/ui/audio-effect/useOpenCloseAudioEffect';
import { useArrowFocusList } from '~/lib/a11y/useArrowFocusList';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';

export const PoliciesButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  border: 1px solid ${p => p.theme.colors.greyMed};
  border-radius: 10px;
  background-color: transparent;
`;

const DocumentsCard = styled(Card)`
  position: relative;
`;

const selector = (s: TrainingState) => select(s, 'policies');

const DocumentsUI = () => {
  const { open, toggle } = useDocumentsStore();
  const { policies } = useTrainingStore(selector);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useOpenCloseAudioEffect(open);
  useFocusTrap(dialogRef, open, titleRef);
  useArrowFocusList(dialogRef, open);

  const close = useCallback(() => {
    toggle(false);
  }, [toggle]);

  // Navigation drawer unmounts this tree while `open` may still be true — reset.
  useEffect(() => () => toggle(false), [toggle]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.stopImmediatePropagation();
      close();
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, close]);

  if (!policies || policies.length === 0) {
    return null;
  }

  return (
    <>
      <PoliciesButton
        type="button"
        onClick={() => toggle(true)}
        aria-label={t`Documents`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <PoliciesIcon aria-hidden="true" focusable="false" />
      </PoliciesButton>
      {open ? (
        <Overlay open allowScroll onClick={close}>
          <Flex
            zIndex="dialogs"
            p={{ _: 'sm', sm: 'xl' }}
            minHeight="100%"
            justifyContent={{ _: 'stretch', sm: 'center' }}
            alignItems={{ _: 'flex-end', sm: 'center' }}
            onClick={event => event.stopPropagation()}
          >
            <DocumentsCard
              w={{ _: '80vw', sm: 'auto' }}
              className="modalStyleForDarkTheme"
              onClick={event => event.stopPropagation()}
            >
              <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={DOCUMENTS_DIALOG_TITLE_ID}
              >
                <CloseButton
                  autoFocus={false}
                  onClick={close}
                  ariaLabel={t`Close`}
                />
                <Root titleRef={titleRef} />
              </div>
            </DocumentsCard>
          </Flex>
        </Overlay>
      ) : null}
    </>
  );
};

export const Documents = () => (
  <DocumentsProvider>
    <DocumentsUI />
  </DocumentsProvider>
);
